import { Link, data } from 'react-router';
import type { Route } from './+types/blog.$slug';
import { buildMeta } from '~/lib/seo';
import { brandingFromMatches, useBranding } from '~/lib/branding';
import { getPost, related } from '~/lib/blog';

/**
 * A single post.
 *
 * The loader throws a real 404 for an unknown slug rather than rendering an empty shell.
 * A soft 404 (200 status, "not found" body) is worse than useless for SEO: Google indexes
 * the empty page and the site accumulates thin content, which is exactly the failure mode
 * the old WordPress blog already had.
 */
export async function loader({ params }: Route.LoaderArgs) {
  const post = getPost(params.slug);
  if (!post) {
    throw data('Not found', { status: 404 });
  }
  return { post, related: related(post) };
}

export const meta: Route.MetaFunction = ({ data: loaded, matches }) => {
  const branding = brandingFromMatches(matches);
  if (!loaded?.post) {
    return [{ title: 'Post not found' }, { name: 'robots', content: 'noindex' }];
  }
  const { post } = loaded;
  return [
    ...buildMeta({
      title: post.title,
      description: post.description,
      path: `/blog/${post.slug}`,
      siteName: branding.name,
    }),
    { property: 'og:type', content: 'article' },
    { property: 'article:published_time', content: post.date },
    ...(post.updated ? [{ property: 'article:modified_time', content: post.updated }] : []),
  ];
};

function formatDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}

export default function BlogPost({ loaderData }: Route.ComponentProps) {
  const { post, related: more } = loaderData;
  const branding = useBranding();

  /*
   * Article JSON-LD. `author` is the ORGANISATION, not a person's name, because a named
   * author who does not exist elsewhere on the web is a fabricated E-E-A-T signal and
   * Google treats an unverifiable byline as worth nothing at best. The organisation is
   * real, has a LocalBusiness entry in root.tsx, and is who actually stands behind the post.
   */
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.updated ?? post.date,
    author: { '@type': 'Organization', name: branding.name },
    publisher: { '@type': 'Organization', name: branding.name },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://nlcstpete.com/blog/${post.slug}` },
  };

  return (
    <main className="blog-post">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <nav className="blog-post-back">
        <Link to="/blog">Back to all posts</Link>
      </nav>

      <article>
        <header className="blog-post-head">
          <h1>{post.title}</h1>
          <p className="blog-post-meta">
            <time dateTime={post.date}>{formatDate(post.date)}</time>
            <span aria-hidden="true"> · </span>
            {post.readingMinutes} min read
          </p>
        </header>

        {/*
          The markdown is ours. It is written into this repo by us and reviewed in a pull
          request before it can ship, so there is no untrusted-input path here and no
          sanitiser. If posts ever come from a database or a form, that stops being true
          and this needs DOMPurify the same way the SOW snapshot does.
        */}
        <div
          className="blog-post-body"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />
      </article>

      {more.length > 0 && (
        <aside className="blog-post-related">
          <h2>Related</h2>
          <ul>
            {more.map((p) => (
              <li key={p.slug}>
                <Link to={`/blog/${p.slug}`}>{p.title}</Link>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <aside className="blog-post-cta">
        <p>
          {branding.name} cleans homes across Pinellas and Hillsborough County. If you want
          a quote, it takes about a minute.
        </p>
        <Link to="/get-a-quote" className="btn btn-primary">
          Get a quote
        </Link>
      </aside>
    </main>
  );
}
